import React, { useEffect, useState } from "react";
import { Button, Image, message, Spin, Empty } from "antd";
import {
  ReloadOutlined,
  DownloadOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import axios from "axios";
import "./MyDownloads.css";

// Reuse token pattern
const getStoredToken = () => {
  const keys = ["token", "accessToken", "AuthToken", "jwtToken"];
  for (const k of keys) {
    const v = localStorage.getItem(k);
    if (v) return v.replace(/^"|"$/g, "");
  }
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.token || user?.accessToken || null;
  } catch {
    return null;
  }
};

const api = axios.create({
  baseURL: global.config.APIURL.BaseURL,
  timeout: 15000,
});
api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = {
      ...(config.headers || {}),
      Authorization: `Bearer ${token}`,
    };
  }
  if (config.withCredentials === undefined) config.withCredentials = false;
  return config;
});

const MyDownloads = () => {
  const [messageApi, contextHolder] = message.useMessage();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);

  const loadData = async (initial = false) => {
    try {
      if (initial) setLoading(true);
      else setFetching(true);

      const res = await api.get("api/MyDownload/GetMyDownloads");
      const data = Array.isArray(res.data) ? res.data : [];
      setRows(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        messageApi.error("Unauthorized. Please log in again.");
      } else if (err?.request && !err?.response) {
        messageApi.error("Network/CORS error while loading downloads.");
      } else {
        messageApi.error("Failed to load download list.");
      }
    } finally {
      setLoading(false);
      setFetching(false);
    }
  };

  useEffect(() => {
    loadData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open a single image URL in a new tab
  const downloadSingle = (url) => {
    if (!url) {
      messageApi.info("No download URL found.");
      return;
    }
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Reusable grid for image previews + per-image download
  const ImagesGrid = ({ record }) => {
    const list = Array.isArray(record?.Images)
      ? record.Images.filter(Boolean)
      : [];
    if (list.length === 0)
      return (
        <span style={{ color: "#bfbfbf", fontSize: 13 }}>No images</span>
      );
    return (
      <div className="download-images">
        {list.map((img, idx) => (
          <div key={`${record.ProdID || "row"}_${idx}`} style={{ width: "100%" }}>
            <Image
              src={img?.DisplayURL || record?.DisplayURL}
              width="100%"
              height={88}
              preview={{ mask: <PictureOutlined /> }}
              style={{
                objectFit: "cover",
                borderRadius: 8,
                border: "1px solid #f0f0f0",
              }}
              fallback={record?.DisplayURL}
            />
            <Button
              block
              size="small"
              type="primary"
              style={{ marginTop: 6 }}
              icon={<DownloadOutlined />}
              onClick={() => downloadSingle(img?.DownloadURLs)}
            >
              Download
            </Button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ background: "#fff" }}>
      {contextHolder}
      <div className="downloads-page">
        <div className="downloads-header">
          <div>
            <h1>My Downloads</h1>
            <p>Download high-resolution product images</p>
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => loadData(false)}
            loading={fetching}
          >
            Refresh
          </Button>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 72 }}>
            <Spin size="large" />
          </div>
        ) : rows.length === 0 ? (
          <Empty
            description="No downloads available"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: "64px 0" }}
          />
        ) : (
          <div className="downloads-grid">
            {rows.map((item) => (
              <div className="download-card" key={item.ProdID}>
                <div className="download-card__design">
                  {item?.DesignNo || "-"}
                </div>
                <div className="download-card__name">{item?.Name || "-"}</div>
                <ImagesGrid record={item} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyDownloads;
